/**
 * Secondary Button Component
 * Alternative action button with gray fill style
 * Uses theme-aware background/text tokens for both light and dark modes
 */

import { memo } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { designTokens } from '../../constants/designTokens';

interface SecondaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const SecondaryButton = memo(function SecondaryButton({
  children,
  fullWidth = false,
  size = 'medium',
  style,
  disabled,
  className = '',
  ...props
}: SecondaryButtonProps) {
  const buttonClass = `
    secondary-button
    secondary-button-${size}
    ${fullWidth ? 'secondary-button-full' : ''}
    ${className}
  `.trim();

  return (
    <>
      <button
        className={buttonClass}
        style={style}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>

      <style>{`
        .secondary-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: ${designTokens.typography.fontFamily.primary};
          font-weight: ${designTokens.typography.fontWeight.semibold};
          border: 1px solid ${designTokens.colors.background.tertiary};
          border-radius: ${designTokens.components.button.borderRadius};
          background-color: ${designTokens.colors.background.secondary};
          color: ${designTokens.colors.text.primary};
          text-align: center;
          cursor: pointer;
          transition: ${designTokens.components.button.transition};
          outline: none;

          /* Touch device optimizations */
          -webkit-tap-highlight-color: transparent;
          user-select: none;
          touch-action: manipulation;
        }

        .secondary-button:disabled {
          color: ${designTokens.colors.text.tertiary};
          cursor: not-allowed;
          opacity: 0.6;
        }

        .secondary-button-small {
          padding: ${designTokens.spacing.sm} ${designTokens.spacing.md};
          font-size: ${designTokens.typography.fontSize.sm};
          min-height: ${designTokens.components.button.height};
        }

        .secondary-button-medium {
          padding: ${designTokens.spacing.md} ${designTokens.spacing.lg};
          font-size: ${designTokens.typography.fontSize.base};
          min-height: ${designTokens.components.button.height};
        }

        .secondary-button-large {
          padding: ${designTokens.spacing.lg} ${designTokens.spacing.xl};
          font-size: ${designTokens.typography.fontSize.lg};
          min-height: ${designTokens.components.button.height};
        }

        .secondary-button:hover:not(:disabled) {
          background-color: ${designTokens.colors.background.tertiary};
        }

        .secondary-button:active:not(:disabled) {
          transform: scale(0.98);
        }

        .secondary-button-full {
          width: 100%;
        }

        ${designTokens.media.mobile} {
          .secondary-button:not(.secondary-button-full) {
            width: ${designTokens.components.button.width.mobile};
            margin: 0 auto;
          }

          .secondary-button-small,
          .secondary-button-medium,
          .secondary-button-large {
            min-height: ${designTokens.components.button.height};
          }
        }

        ${designTokens.media.tablet} {
          .secondary-button:not(.secondary-button-full) {
            width: ${designTokens.components.button.width.tablet};
            margin: 0 auto;
          }

          .secondary-button-small,
          .secondary-button-medium,
          .secondary-button-large {
            min-height: ${designTokens.components.button.height};
          }
        }

        ${designTokens.media.desktop} {
          .secondary-button:not(.secondary-button-full) {
            width: ${designTokens.components.button.width.desktop};
            margin: 0 auto;
          }

          .secondary-button-small,
          .secondary-button-medium,
          .secondary-button-large {
            min-height: ${designTokens.components.button.height};
          }
        }

        .secondary-button:focus-visible {
          box-shadow: 0 0 0 3px ${designTokens.colors.withOpacity.brandFocus};
        }
      `}</style>
    </>
  );
});
